/**
 * SYB Shipping Bar Custom Element
 * - Updates dynamically when cart changes via pub/sub
 * - Supports dismissal with 24-hour localStorage persistence
 */

class SybShippingBar extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.dismissible = this.dataset.dismissible === 'true';
    this.storageKey = 'syb-shipping-bar-dismissed';
  }

  connectedCallback() {
    // Check if previously dismissed
    if (this.isDismissed()) {
      this.hide();
      return;
    }

    // Bind close button if dismissible
    if (this.dismissible) {
      const closeButton = this.querySelector('.syb-shipping-bar__close');
      if (closeButton) {
        closeButton.addEventListener('click', this.dismiss.bind(this));
      }
    }

    // Subscribe to cart updates
    if (typeof subscribe === 'function') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
    }
  }

  disconnectedCallback() {
    // Clean up subscription when element is removed
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  /**
   * Handle cart update events
   */
  onCartUpdate() {
    // Add updating class for animation
    this.classList.add('syb-shipping-bar--updating');

    // Fetch updated section HTML
    this.fetchSection()
      .then((html) => {
        this.updateContent(html);
        // Remove updating class after animation
        setTimeout(() => {
          this.classList.remove('syb-shipping-bar--updating');
        }, 300);
      })
      .catch((error) => {
        console.error('SYB Shipping Bar: Error fetching section', error);
        this.classList.remove('syb-shipping-bar--updating');
      });
  }

  /**
   * Fetch the section HTML from the server
   * @returns {Promise<string>}
   */
  fetchSection() {
    if (this.fetchController) this.fetchController.abort();
    this.fetchController = new AbortController();

    const sectionUrl = `${window.routes.cart_url}?section_id=syb-shipping-bar`;
    return fetch(sectionUrl, { signal: this.fetchController.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
      });
  }

  /**
   * Update the DOM with new section content
   * @param {string} html
   */
  updateContent(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newBar = doc.querySelector('syb-shipping-bar');

    if (newBar) {
      // Update the inner HTML while preserving the element
      const contentWrapper = this.querySelector('.syb-shipping-bar__content');
      const newContentWrapper = newBar.querySelector('.syb-shipping-bar__content');

      if (contentWrapper && newContentWrapper) {
        contentWrapper.innerHTML = newContentWrapper.innerHTML;
      }

      // Update qualified class
      if (newBar.classList.contains('syb-shipping-bar--qualified')) {
        this.classList.add('syb-shipping-bar--qualified');
      } else {
        this.classList.remove('syb-shipping-bar--qualified');
      }

      // Update progress bar attributes if present
      const progressBar = this.querySelector('.syb-shipping-bar__progress');
      const newProgressBar = newBar.querySelector('.syb-shipping-bar__progress');
      if (progressBar && newProgressBar) {
        progressBar.setAttribute('aria-valuenow', newProgressBar.getAttribute('aria-valuenow'));
      }
    }
  }

  /**
   * Dismiss the shipping bar
   */
  dismiss() {
    // Set dismissal time in localStorage (expires after 24 hours)
    const expiryTime = Date.now() + (24 * 60 * 60 * 1000);
    localStorage.setItem(this.storageKey, expiryTime.toString());
    this.hide();
  }

  /**
   * Check if the bar was previously dismissed
   * @returns {boolean}
   */
  isDismissed() {
    const expiry = localStorage.getItem(this.storageKey);
    if (!expiry) return false;

    const expiryTime = parseInt(expiry, 10);
    if (Date.now() > expiryTime) {
      // Expired, remove from storage
      localStorage.removeItem(this.storageKey);
      return false;
    }
    return true;
  }

  /**
   * Hide the shipping bar
   */
  hide() {
    this.setAttribute('hidden', '');
  }

  /**
   * Show the shipping bar
   */
  show() {
    this.removeAttribute('hidden');
  }
}

// Define the custom element
if (!customElements.get('syb-shipping-bar')) {
  customElements.define('syb-shipping-bar', SybShippingBar);
}

/**
 * Detect Klaviyo popup/flyout forms and add body class for CSS handling.
 *
 * Klaviyo creates TWO separate root containers (direct children of <body>)
 * for a single popup campaign: one for the backdrop and one for the content.
 * The close button only dismisses the content container, leaving the backdrop
 * visible and requiring a second click. We detect this "partial close" state
 * (one container collapsed to 0×0, the other still full-viewport) and
 * force-hide the remaining container.
 */
(function () {
  var timer;

  function isKlaviyoPopup(el) {
    if (!el || !el.className || typeof el.className !== 'string') return false;
    if (el.className.indexOf('klaviyo-form') === -1) return false;
    var node = el.parentElement;
    while (node && node !== document.body) {
      if (window.getComputedStyle(node).position === 'fixed') return true;
      node = node.parentElement;
    }
    return false;
  }

  function getRootOf(el) {
    var node = el;
    while (node && node.parentElement && node.parentElement !== document.body) {
      node = node.parentElement;
    }
    return (node && node.parentElement === document.body) ? node : null;
  }

  function check() {
    var forms = document.querySelectorAll('[class*="klaviyo-form"]');

    // Collect all popup forms (forms with a fixed-position ancestor)
    var popups = [];
    for (var i = 0; i < forms.length; i++) {
      if (!isKlaviyoPopup(forms[i])) continue;
      var root = getRootOf(forms[i]);
      if (!root) continue;
      var rect = forms[i].getBoundingClientRect();
      popups.push({ root: root, hasSize: rect.width > 0 && rect.height > 0 });
    }

    // Detect partial close: one popup collapsed, another still visible.
    // This happens when Klaviyo's close button dismisses only the content
    // container but leaves the backdrop container active.
    var anyCollapsed = false;
    var anyVisible = false;
    for (var i = 0; i < popups.length; i++) {
      if (popups[i].hasSize) anyVisible = true;
      else anyCollapsed = true;
    }
    if (anyCollapsed && anyVisible) {
      for (var i = 0; i < popups.length; i++) {
        if (popups[i].hasSize) {
          popups[i].root.style.display = 'none';
        }
      }
      anyVisible = false;
    }

    if (anyVisible) {
      document.body.classList.add('klaviyo-popup-active');
    } else {
      document.body.classList.remove('klaviyo-popup-active');
    }
  }

  function debouncedCheck() {
    clearTimeout(timer);
    timer = setTimeout(check, 16);
  }

  new MutationObserver(debouncedCheck).observe(document.body, { childList: true, subtree: true });
})();

